/**
 * Which page is showing, from the address: the events, one event's layouts, a layout in the hall, a
 * view-only link, the people list, or the page a set-up link opens. Moving between pages changes the
 * address without reloading, so the browser's back button works and any page can be bookmarked.
 */

import { create } from 'zustand';

export type Route =
  | { page: 'events' }
  | { page: 'event'; id: string }
  | { page: 'layout'; id: string }
  | { page: 'view'; token: string }
  | { page: 'people' }
  | { page: 'welcome' }
  | { page: 'missing' };

const ID = '([0-9a-hjkmnp-tv-z]{16})';

export function routeOf(path: string): Route {
  const p = path.replace(/\/+$/, '') || '/';
  if (p === '/') return { page: 'events' };
  if (p === '/people') return { page: 'people' };
  if (p === '/welcome') return { page: 'welcome' };
  let m = new RegExp(`^/e/${ID}$`).exec(p);
  if (m) return { page: 'event', id: m[1]! };
  m = new RegExp(`^/l/${ID}$`).exec(p);
  if (m) return { page: 'layout', id: m[1]! };
  m = /^\/v\/([A-Za-z0-9_-]{43})$/.exec(p);
  if (m) return { page: 'view', token: m[1]! };
  return { page: 'missing' };
}

interface RouteState {
  path: string;
  /** Asked before leaving a page with unsaved changes; returns false to stay. */
  guard: ((to: string) => boolean) | null;
}

export const useRoute = create<RouteState>(() => ({ path: location.pathname, guard: null }));

export function navigate(to: string, opts: { replace?: boolean; force?: boolean } = {}) {
  const { guard, path } = useRoute.getState();
  if (to === path + location.search) return;
  if (!opts.force && guard && !guard(to)) return;
  if (opts.replace) history.replaceState(null, '', to);
  else history.pushState(null, '', to);
  useRoute.setState({ path: location.pathname });
  scrollTo(0, 0);
}

addEventListener('popstate', () => {
  const { guard, path } = useRoute.getState();
  if (guard && !guard(location.pathname)) {
    // stay: put the address back
    history.pushState(null, '', path);
    return;
  }
  useRoute.setState({ path: location.pathname });
});

export function setGuard(guard: RouteState['guard']) {
  useRoute.setState({ guard });
}
